import { ExpenseDocumentTypeEnum } from "@finway-group/shared/lib/models/expense/expenseDocumentType.enum"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { isAtLeastReviewed } from "@finway-group/shared/lib/utils/expense.utils"
import { Col, Form, Radio, Row, Tooltip } from "antd"
import { type RadioChangeEvent } from "antd/lib/radio"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useExpenseFormContext } from "~Components/modals/expenseCreateForm.context"
import { useCompany } from "~Shared/hooks/company.hooks"
import { canBeCreditNote, getCreditNoteDisabledReason, getLinkedPurchaseOrders } from "~Shared/utils/expense.utils"

export const DocumentTypeSelector = () => {
    const [{ expense, updateExpense, setIsDocumentTypeSetByUser }, , { selectedPurchaseOrders }] = useExpenseFormContext()
    const company = useCompany()
    const { t } = useTranslation()
    const isCreditNoteAllowed = canBeCreditNote(expense)

    // e.g. a credit note turned into a recurring expense
    useEffect(() => {
        if (!isCreditNoteAllowed && expense.documentType === ExpenseDocumentTypeEnum.CREDIT_NOTE) {
            updateExpense({ documentType: ExpenseDocumentTypeEnum.INVOICE })
        }
    }, [isCreditNoteAllowed, expense.documentType])

    if (!company?.vendorPaymentNettingEnabled || expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD || !isCreditNoteAllowed) {
        return null
    }

    const documentType = expense.documentType ?? ExpenseDocumentTypeEnum.INVOICE
    const isLocked = isAtLeastReviewed(expense.status)
    const hasPurchaseOrders = selectedPurchaseOrders.length > 0 || getLinkedPurchaseOrders(expense).length > 0
    const creditNoteDisabledReason = getCreditNoteDisabledReason(expense, hasPurchaseOrders)
    const onDocumentTypeChange = ({ target: { value } }: RadioChangeEvent) => {
        setIsDocumentTypeSetByUser(true)
        updateExpense({ documentType: value })
    }

    return (
        <Row gutter={[16, 16]} align="middle">
            <Col span={24}>
                <Form.Item
                    className="max-w-400"
                    label={t("input:request.document_type.label")}
                    extra={documentType === ExpenseDocumentTypeEnum.CREDIT_NOTE ? t("info:credit_note_negative_amount") : undefined}
                >
                    <Tooltip title={isLocked ? t("tooltips:expenses.document_type_locked_after_review") : undefined}>
                        <div className="card-modal">
                            <Radio.Group value={documentType} onChange={onDocumentTypeChange} name="documentType" disabled={isLocked}>
                                <Radio.Button value={ExpenseDocumentTypeEnum.INVOICE}>
                                    <p className="truncate">{t("input:request.document_type.invoice")}</p>
                                </Radio.Button>
                                <Tooltip title={creditNoteDisabledReason && t(creditNoteDisabledReason)}>
                                    <Radio.Button value={ExpenseDocumentTypeEnum.CREDIT_NOTE} disabled={isLocked || !!creditNoteDisabledReason} data-testid="expenseFormCreditNote">
                                        <p className="truncate">{t("input:request.document_type.credit_note")}</p>
                                    </Radio.Button>
                                </Tooltip>
                            </Radio.Group>
                        </div>
                    </Tooltip>
                </Form.Item>
            </Col>
        </Row>
    )
}
